// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {getSharedVoiceAudioContext} from '@app/features/voice/engine/VoiceSharedAudioContext';
import {parseVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {AMBIENCE_DUCK_GAIN, createRoomAmbience, type RoomAmbience} from '@app/features/voice_scene/audio/RoomAmbience';
import {
	createRoomEmitters,
	distanceLevel,
	type RoomEmitters,
	soundPlace,
} from '@app/features/voice_scene/audio/RoomEmitters';
import {createRoomOneshots} from '@app/features/voice_scene/audio/RoomOneshots';
import {createRoomSfx, type RoomSfx} from '@app/features/voice_scene/audio/RoomSfx';
import {
	computeSpeakerAudioParams,
	FLAT_SPEAKER_PARAMS,
	SPEAKER_GAIN,
	spatialSpeakerKey,
} from '@app/features/voice_scene/audio/SpatialAudioMath';
import type {RoomPoseSource} from '@app/features/voice_scene/hooks/usePositionSender';
import {roomListener} from '@app/features/voice_scene/hooks/useSpatialAudioSync';
import {RoomPositions} from '@app/features/voice_scene/net/PositionBuffer';
import {SLAP_CONTACT_MS} from '@app/features/voice_scene/scene/avatar/AvatarAnimator';
import type {RoomEmote, Vec3, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomAvatarSignals from '@app/features/voice_scene/state/RoomAvatarSignals';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import type {SeatAssignment, SeatParticipant} from '@app/features/voice_scene/utils/SeatAssignment';
import {
	VOICE_ROOM_AMBIENCE,
	VOICE_ROOM_SCENE_SOUNDS,
	VOICE_ROOM_SFX,
	type VoiceRoomSfx,
} from '@fluxer/constants/src/VoiceRoomSounds';
import type {VoiceRoomTheme} from '@fluxer/constants/src/VoiceRoomThemes';
import {type Participant, type RemoteParticipant, type Room, RoomEvent} from 'livekit-client';
import {reaction} from 'mobx';
import {useCallback, useEffect, useRef, useState} from 'react';

// Sound files sit next to the room files under the static CDN, by their hashed manifest names only.
const roomSoundUrl = (file: string): string => `${RuntimeConfig.staticCdnEndpoint}/rooms/${file}`;

// The room's point loops follow my seat, my walk and the camera this often (their ramps smooth in between).
export const SCENE_SOUND_INTERVAL_MS = 250;

// An answer this late after the contact would sound after the head is thrown: it plays nothing.
const SLAP_LATE_MS = 250;

const emoteSound = (emote: RoomEmote): VoiceRoomSfx | null =>
	emote.kind === 'wave' ? 'wave' : emote.kind === 'emoji' || emote.kind === 'custom' ? 'reaction' : null;

interface Places {
	handle: VoiceSceneHandle;
	assignment: SeatAssignment<SeatParticipant>;
	myUserId: string | null;
	walk: RoomPoseSource | null;
}

// Where a user's event sounds, in the listener's frame the voices use: my seat, turned with the camera.
function listenerPoint({handle, assignment, myUserId}: Places, userId: string): Vec3 {
	if (!VoiceSceneSettings.spatialAudio) return FLAT_SPEAKER_PARAMS.position;
	const {layout} = handle;
	const own = myUserId ? assignment.placements.get(myUserId)?.position : undefined;
	const params = computeSpeakerAudioParams(
		{
			listener: {
				position: own ?? layout.camTarget,
				azimuth: handle.getCameraAzimuth(),
				home: {camHome: layout.camHome, camTarget: layout.camTarget},
			},
			speakers: [
				{identity: userId, kind: 'mic', position: assignment.placements.get(userId)?.position ?? layout.camTarget},
			],
		},
		'spatial',
	);
	return params.values().next().value?.position ?? FLAT_SPEAKER_PARAMS.position;
}

// Where a slap lands: on the slapped person's character while it walks, placed and quieter with distance like their
// footsteps (useWalkAudio); seated, where the other event sounds are.
function slapPlace(places: Places, identity: string, userId: string, now: number): {position: Vec3; level: number} {
	const {handle, assignment, myUserId, walk} = places;
	const mine = walk?.getPose() ?? null;
	const pose = userId === myUserId ? mine : RoomPositions.pose(identity, now);
	if (!pose) return {position: listenerPoint(places, userId), level: 1};
	if (!VoiceSceneSettings.spatialAudio) return {position: FLAT_SPEAKER_PARAMS.position, level: 1};
	const own = myUserId ? assignment.placements.get(myUserId)?.position : undefined;
	const at = computeSpeakerAudioParams(
		{
			listener: roomListener(handle, own, mine),
			speakers: [{identity, kind: 'mic', position: {x: pose.x, y: pose.y, z: pose.z}, walking: true}],
		},
		'spatial',
	).get(spatialSpeakerKey(identity, 'mic'))!;
	return {position: at.position, level: at.gain / SPEAKER_GAIN};
}

interface RoomAudio {
	ambience: RoomAmbience | null;
	sfx: RoomSfx;
	emitters: RoomEmitters | null;
}

const randomDirection = (): Vec3 => {
	const angle = Math.random() * 2 * Math.PI;
	return {x: Math.sin(angle), y: 0, z: -Math.cos(angle)};
};

// The room's own sound: its ambience loop if it has one (ducked while anyone talks), its scene sounds
// (VOICE_ROOM_SCENE_SOUNDS: loops at ses_ nodes, quieter with distance, and now-and-then sounds, all on the ambience's
// volume) and event sounds where they happen (someone coming in, leaving, taking a seat, waving, reacting). Everything
// plays on the shared voice context and goes quiet while this user is deafened, by themselves or by a moderator (the
// effective deafen Fluxer's own sounds follow); the room view closing lets go of every node, buffer and timer. A scene
// sound whose node is not in the room stays silent. Hands out its event sound player: the room's one, the walking
// sounds play through it too (null without a room theme or audio). walk: my pose source while walking is on (my ears go
// where I walk).
export function useRoomAmbience({
	handle,
	room,
	theme,
	assignment,
	myUserId,
	walk,
}: {
	handle: VoiceSceneHandle;
	room: Room | null;
	theme: VoiceRoomTheme | null;
	assignment: SeatAssignment<SeatParticipant>;
	myUserId: string | null;
	walk: RoomPoseSource | null;
}): {sfx: RoomSfx | null; setScreenPlaying(playing: boolean): void} {
	const [audio, setAudio] = useState<RoomAudio | null>(null);
	const latest = useRef<Places>({handle, assignment, myUserId, walk});
	latest.current = {handle, assignment, myUserId, walk};
	const ducked = useRef(false);
	// A cinema's ambience follows the TV's playing signal (the one that lowers its house lights).
	const screenPlaying = useRef(false);
	const cinemaAmbience = useRef<RoomAmbience | null>(null);
	const setScreenPlaying = useCallback((playing: boolean): void => {
		screenPlaying.current = playing;
		cinemaAmbience.current?.setScreenPlaying(playing);
	}, []);

	useEffect(() => {
		const context = theme ? getSharedVoiceAudioContext() : null;
		if (!theme || !context) return;
		const ambienceSound = VOICE_ROOM_AMBIENCE[theme];
		const ambience = ambienceSound
			? createRoomAmbience({
					context,
					sound: {url: roomSoundUrl(ambienceSound.file), gain: ambienceSound.gain},
					volume: VoiceSceneSettings.ambienceVolume,
				})
			: null;
		if (ambience && theme === 'sinema') {
			cinemaAmbience.current = ambience;
			if (screenPlaying.current) ambience.setScreenPlaying(true);
		}
		const soundOf = (name: VoiceRoomSfx) => ({
			url: roomSoundUrl(VOICE_ROOM_SFX[name].file),
			gain: VOICE_ROOM_SFX[name].gain,
		});
		const eventSfx = createRoomSfx({context, soundOf});
		const scene = VOICE_ROOM_SCENE_SOUNDS[theme];
		const sitSounds = scene?.sit ?? [];
		let sits = 0;
		const sfx: RoomSfx = sitSounds.length
			? {
					...eventSfx,
					play: (name, at, sender, shot) =>
						eventSfx.play(name === 'sit' ? sitSounds[sits++ % sitSounds.length]! : name, at, sender, shot),
				}
			: eventSfx;

		const listener = () => {
			const {handle, assignment, myUserId, walk} = latest.current;
			const own = myUserId ? assignment.placements.get(myUserId)?.position : undefined;
			return roomListener(handle, own, walk?.getPose() ?? null);
		};
		const pointOf = (node: string): Vec3 | undefined => latest.current.handle.layout.soundPoints?.[node];

		const emitterSounds = (scene?.emitters ?? []).filter(({node}) => pointOf(node));
		const emitters = emitterSounds.length
			? createRoomEmitters({
					context,
					sounds: emitterSounds.map(({sound, refDistance, maxDistance}) => ({
						url: roomSoundUrl(sound.file),
						gain: sound.gain,
						refDistance,
						maxDistance,
					})),
					volume: VoiceSceneSettings.ambienceVolume,
				})
			: null;
		const placeEmitters = (): void => {
			const ears = listener();
			emitters?.place(
				emitterSounds.map(({node}) => {
					const point = pointOf(node);
					return point ? soundPlace(point, ears, VoiceSceneSettings.spatialAudio) : null;
				}),
			);
		};
		let placing: ReturnType<typeof setInterval> | null = null;
		// Placed now and then while the tab shows; a hidden tab has no timer.
		const placeWhileShown = (): void => {
			if (placing !== null) clearInterval(placing);
			placing = null;
			if (!emitters || document.hidden) return;
			placeEmitters();
			placing = setInterval(placeEmitters, SCENE_SOUND_INTERVAL_MS);
		};

		const oneshotGroups = scene?.oneshots ?? [];
		const sceneSfx = oneshotGroups.length ? createRoomSfx({context, soundOf, busGain: 1}) : null;
		const oneshots = sceneSfx
			? createRoomOneshots({
					groups: oneshotGroups,
					play: ({sounds, at}, index) => {
						let level = VoiceSceneSettings.ambienceVolume * (ducked.current ? AMBIENCE_DUCK_GAIN : 1);
						let position = VoiceSceneSettings.spatialAudio ? randomDirection() : FLAT_SPEAKER_PARAMS.position;
						if (at) {
							const point = pointOf(at.node);
							if (!point) return;
							const place = soundPlace(point, listener(), VoiceSceneSettings.spatialAudio);
							level *= distanceLevel(place.distance, at.refDistance, at.maxDistance);
							position = place.position;
						}
						if (level <= 0) return;
						const sound = sounds[Math.floor(Math.random() * sounds.length)]!;
						sceneSfx.play(sound, position, `scene:${index}`, {level});
					},
				})
			: null;
		// The few short sounds that play first after a long wait download ahead (they would go stale on a slow network).
		const warm = (): void => {
			// A slap's landing is due a roundtrip after it is seen, sooner than its file would download.
			eventSfx.warm([...sitSounds, 'slap']);
			if (VoiceSceneSettings.ambienceVolume > 0)
				sceneSfx?.warm(oneshotGroups.flatMap(({sounds, at}) => (!at || pointOf(at.node) ? sounds : [])));
		};

		const stopVolume = reaction(
			() => VoiceSceneSettings.ambienceVolume,
			(volume) => {
				ambience?.setVolume(volume);
				emitters?.setVolume(volume);
				warm();
			},
		);
		const stopDeaf = reaction(
			() => getEffectiveAudioState().effectiveDeaf,
			(deafened) => {
				ambience?.setDeafened(deafened);
				sfx.setDeafened(deafened);
				emitters?.setDeafened(deafened);
				sceneSfx?.setDeafened(deafened);
				warm();
			},
			{fireImmediately: true},
		);
		if (emitters) document.addEventListener('visibilitychange', placeWhileShown);
		placeWhileShown();
		const stopSignals = RoomAvatarSignals.subscribe((signal) => {
			if (!('emote' in signal)) return;
			const {userId} = parseVoiceParticipantIdentity(signal.identity);
			if (signal.emote.kind === 'slapped') {
				const now = performance.now();
				const untilContact = (signal.at ?? now) + SLAP_CONTACT_MS - now;
				if (untilContact < -SLAP_LATE_MS) return;
				const {position, level} = slapPlace(latest.current, signal.identity, userId, now);
				sfx.play('slap', position, `${signal.identity}:slap`, {delayS: Math.max(0, untilContact) / 1000, level});
				return;
			}
			const sound = emoteSound(signal.emote);
			if (sound) sfx.play(sound, listenerPoint(latest.current, userId), signal.identity);
		});
		setAudio({ambience, sfx, emitters});
		return () => {
			stopVolume();
			stopDeaf();
			stopSignals();
			document.removeEventListener('visibilitychange', placeWhileShown);
			if (placing !== null) clearInterval(placing);
			oneshots?.dispose();
			cinemaAmbience.current = null;
			ambience?.dispose();
			eventSfx.dispose();
			emitters?.dispose();
			sceneSfx?.dispose();
			setAudio(null);
		};
	}, [theme]);

	useEffect(() => {
		if (!audio || !room) return;
		const {ambience, sfx, emitters} = audio;
		const onSpeakers = (speakers: Array<Participant>): void => {
			ducked.current = speakers.length > 0;
			ambience?.setDucked(ducked.current);
			emitters?.setDucked(ducked.current);
		};
		const at = (participant: RemoteParticipant) =>
			listenerPoint(latest.current, parseVoiceParticipantIdentity(participant.identity).userId);
		const onJoined = (participant: RemoteParticipant): void => sfx.play('join', at(participant), participant.identity);
		const onLeft = (participant: RemoteParticipant): void => sfx.play('leave', at(participant), participant.identity);
		onSpeakers(room.activeSpeakers);
		room.on(RoomEvent.ActiveSpeakersChanged, onSpeakers);
		room.on(RoomEvent.ParticipantConnected, onJoined);
		room.on(RoomEvent.ParticipantDisconnected, onLeft);
		return () => {
			room.off(RoomEvent.ActiveSpeakersChanged, onSpeakers);
			room.off(RoomEvent.ParticipantConnected, onJoined);
			room.off(RoomEvent.ParticipantDisconnected, onLeft);
			ducked.current = false;
			ambience?.setDucked(false);
			emitters?.setDucked(false);
		};
	}, [audio, room]);

	// A seat change is the one sit we can hear today; a first placement is the join's.
	const seats = useRef(new Map<string, string | null>());
	useEffect(() => {
		const previous = seats.current;
		const next = new Map<string, string | null>();
		for (const [userId, placement] of assignment.placements) {
			next.set(userId, placement.seat);
			const before = previous.get(userId);
			if (audio && before !== undefined && placement.seat !== null && before !== placement.seat)
				audio.sfx.play('sit', listenerPoint(latest.current, userId), userId);
		}
		seats.current = next;
	}, [assignment, audio]);

	return {sfx: audio?.sfx ?? null, setScreenPlaying};
}
